//@flow
import * as React from 'react';
import { List } from 'immutable';
import {
    Box,
    Button,
    Heading,
    Divider,
    Input,
    Columns
} from '@locus-taxy/neo-ui';
import { useTranslation } from 'react-i18next';
import { MasterOutlet } from 'common/models/internal/MasterOutlet';

type Props = {
    title: string,
    list: ?List<MasterOutlet>,
    onAddClick?: () => void,
    minWidth?: number,
    keyExtractor: (itemObject: MasterOutlet, index: number) => string,
    renderItem: (itemObject: MasterOutlet) => React.Node,
    overflowContainerHeight?: string,
    onItemClick?: (id: string) => void
};

const CollapsibleList = (props: {
    items: ?List<MasterOutlet>,
    renderItem: (itemObject: MasterOutlet) => React.Node,
    keyExtractor: (itemObject: MasterOutlet, index: number) => string,
    overflowContainerHeight?: string
}) => {
    const [searchKey, setSearchKey] = React.useState('');
    const { t } = useTranslation('entityManagement');
    const items = React.useMemo(() => {
        if (!props.items) {
            return [];
        }
        if (searchKey) {
            return props.items.filter(item => {
                if (item.id.toLowerCase().includes(searchKey.toLowerCase())) {
                    return true;
                }
                if (item.name.toLowerCase().includes(searchKey.toLowerCase())) {
                    return true;
                }
                if (item.code.toLowerCase().includes(searchKey.toLowerCase())) {
                    return true;
                }
                return false;
            });
        }
        return props.items;
    }, [props.items, searchKey]);

    const onChangeSearch = React.useCallback(e => {
        setSearchKey(e.target.value);
    }, []);

    return (
        <>
            <Input
                placeholder={t(
                    'createTerritory.territoryEntitySeachPlaceholder'
                )}
                onChange={onChangeSearch}
                value={searchKey}
                border={0}
            />
            <Box
                overflowY="auto"
                height={
                    props.overflowContainerHeight || 'calc(100% - 32px - 40px)'
                }
            >
                {items.map((listItem, index) => {
                    const ItemComponent = props.renderItem(listItem);
                    return React.cloneElement(ItemComponent, {
                        key: props.keyExtractor(listItem, index)
                    });
                })}
            </Box>
        </>
    );
};

const HoveredCollapsibleLists = (props: Props): React.Node => {
    const { t } = useTranslation('entityManagement');
    return (
        <>
            <Box bg="blue.500" pl={3}>
                <Columns alignY="center">
                    <Heading level="4" color="white" py={2}>
                        {` ${props.title} (${props.list?.size || 0})`}
                    </Heading>
                    {props.onAddClick && (
                        <Button onClick={props.onAddClick} color="white" ml={2}>
                            +{t('translations:add')}
                        </Button>
                    )}
                </Columns>
            </Box>
            <Divider color="grey.200" />
            <CollapsibleList
                items={props.list}
                renderItem={props.renderItem}
                keyExtractor={props.keyExtractor}
                overflowContainerHeight={props.overflowContainerHeight}
            />
        </>
    );
};

export default HoveredCollapsibleLists;
